import classNames from 'classnames';
import { FC, ReactElement, useEffect } from 'react';
import { FormSpy } from 'react-final-form';

import {
  Form,
  IFormContentProps,
  Field,
  ImageType,
  ImageUploader,
  TextField,
} from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';
import { AvatarTypes, Button, ButtonTypes, customScrollStyles, PopupFooter } from '@gem/ui-kit';

import { Avatar } from 'APP/components/Avatar/Avatar';
import { ValidationResult } from 'APP/packages/validations';
import { useSpaceCollectionValidations } from 'APP/packages/validations/forms/forms';

import { useSpaceCollectionFormPresenter } from './SpaceCollectionForm.presenter';
import styles from './SpaceCollectionForm.styles.m.css';
import { ICollectionFormData } from './SpaceCollectionForm.types';

const FORM_ID = 'collection-form';
const COLLECTION_NAME_MAX_LENGTH = 35;

interface ICreateNewCollectionProps {
  initialValues?: ICollectionFormData;
  onFormStateChange(state: Partial<IFormContentProps> | null): void;
  onSubmit(data: ICollectionFormData): Promise<void>;
}

export const SpaceCollectionForm: FC<ICreateNewCollectionProps> = ({
  initialValues,
  onFormStateChange,
  onSubmit,
}) => {
  const presenter = useSpaceCollectionFormPresenter();
  const { t } = useTranslation();
  const formValidations = useSpaceCollectionValidations();

  useEffect(() => {
    return () => onFormStateChange(null);
  }, []);

  return (
    <Form
      initialValues={initialValues}
      subscription={{
        submitting: true,
        pristine: true,
        invalid: true,
        validating: true,
      }}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        submitting,
        pristine,
        invalid,
        validating,
      }: IFormContentProps): ReactElement => (
        <>
          <FormSpy
            subscription={{ submitting: true, pristine: true }}
            onChange={onFormStateChange}
          />

          <form
            id={FORM_ID}
            className={classNames(styles.form, customScrollStyles.container)}
            onSubmit={handleSubmit}
          >
            <Field
              name="avatarUrl"
              component={ImageUploader}
              imageType={ImageType.SQUARE}
              validate={(value, formData, meta): ValidationResult => {
                return formValidations.avatar.validate(value ?? '', formData, meta);
              }}
              onLoad={presenter.onImageLoad}
              renderImage={(url: string): ReactElement => (
                <Avatar
                  url={url}
                  type={AvatarTypes.Square_50}
                />
              )}
            />

            <Field
              name="name"
              label={t('space_collection_name_hint')}
              isNeedTrim
              component={TextField}
              maxLength={COLLECTION_NAME_MAX_LENGTH}
              validate={formValidations.name.validate}
            />
          </form>

          <PopupFooter>
            <Button
              type={ButtonTypes.Submit}
              form={FORM_ID}
              isLoading={submitting}
              isDisabled={pristine || invalid || validating}
              theme="primary"
            >
              {initialValues ? t('save') : t('common_create')}
            </Button>
          </PopupFooter>
        </>
      )}
    </Form>
  );
};
